// Single source of truth for backtest node category presentation
// (label, accent color, matching Tailwind text class, icon, display order).
//
// Consumed by:
//   - components/canvas/command-bar.tsx (Tab quick-add command bar)
//   - components/canvas/command-bar/command-bar-item.tsx (command bar rows)
//   - components/panels/dynamic-node-palette.tsx (sidebar node library)
//
// The hex values here are mirrored in backtest-reactflow.css as the
// --bt-node-* tokens that color rendered ReactFlow nodes. If you change a color
// here, update that token too: backtest-theme.unit.test.ts checks every node
// border against this config, and node-category-config.unit.test.ts pins the
// canonical hex.

import {
  ArrowRightLeft,
  Bug,
  Calculator,
  Database,
  Filter,
  GitBranch,
  Settings,
  TrendingUp,
  Wrench,
  type LucideIcon,
} from 'lucide-react'

export type NodeCategory =
  | 'data'
  | 'indicators'
  | 'logic'
  | 'math'
  | 'entry-exit-logic'
  | 'utilities'
  | 'debug'
  | 'filter'
  | 'presets'

export interface NodeCategoryConfig {
  label: string
  // Canonical accent color. Tailwind 500-shade default values are used so that
  // `color` (hex) and `textClass` (Tailwind class) render identically.
  color: string
  textClass: string
  icon: LucideIcon
  order: number
}

export const NODE_CATEGORY_CONFIG: Record<NodeCategory, NodeCategoryConfig> = {
  presets: {
    label: 'Presets',
    color: '#f59e0b',
    textClass: 'text-amber-500',
    icon: Settings,
    order: 0,
  },
  data: {
    label: 'Data Sources',
    color: '#3b82f6',
    textClass: 'text-blue-500',
    icon: Database,
    order: 1,
  },
  indicators: {
    label: 'Indicators',
    color: '#10b981',
    textClass: 'text-emerald-500',
    icon: TrendingUp,
    order: 2,
  },
  logic: {
    label: 'Logic',
    color: '#f59e0b',
    textClass: 'text-amber-500',
    icon: GitBranch,
    order: 3,
  },
  filter: {
    label: 'Filters',
    color: '#06b6d4',
    textClass: 'text-cyan-500',
    icon: Filter,
    order: 4,
  },
  math: {
    label: 'Math',
    color: '#ec4899',
    textClass: 'text-pink-500',
    icon: Calculator,
    order: 5,
  },
  utilities: {
    label: 'Utilities',
    color: '#84cc16',
    textClass: 'text-lime-500',
    icon: Wrench,
    order: 6,
  },
  'entry-exit-logic': {
    label: 'Entry/Exit Logic',
    color: '#ef4444',
    textClass: 'text-red-500',
    icon: ArrowRightLeft,
    order: 7,
  },
  debug: {
    label: 'Debug & Analysis',
    color: '#8b5cf6',
    textClass: 'text-violet-500',
    icon: Bug,
    order: 8,
  },
}

export const FALLBACK_CATEGORY_COLOR = '#6b7280'
export const FALLBACK_CATEGORY_TEXT_CLASS = 'text-gray-400'

export function getCategoryConfig(category: string): NodeCategoryConfig | undefined {
  return NODE_CATEGORY_CONFIG[category as NodeCategory]
}

export function getCategoryColor(category: string): string {
  return getCategoryConfig(category)?.color ?? FALLBACK_CATEGORY_COLOR
}

export function getCategoryTextClass(category: string): string {
  return getCategoryConfig(category)?.textClass ?? FALLBACK_CATEGORY_TEXT_CLASS
}
